import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, TrendingUp, Calendar, Star, Search, Loader2, Trash2,
  ExternalLink, BarChart3, Database, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_BADGE = {
  direct: "bg-blue-50 text-blue-600 border-blue-200",
  google: "bg-red-50 text-red-600 border-red-200",
  facebook: "bg-indigo-50 text-indigo-600 border-indigo-200",
  linkedin: "bg-sky-50 text-sky-600 border-sky-200",
  email: "bg-amber-50 text-amber-600 border-amber-200",
  referral: "bg-emerald-50 text-emerald-600 border-emerald-200",
};

export default function OverviewTab({ adminToken }) {
  const navigate = useNavigate();
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPagination, setLeadsPagination] = useState({ total: 0, pages: 1 });
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchStats = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${adminToken}` } }); setStats(res.data); }
    catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [adminToken]);

  const fetchLeads = useCallback(async () => {
    try {
      const params = { page: leadsPage, limit: 20 };
      if (sourceFilter && sourceFilter !== "all") params.source = sourceFilter;
      const res = await axios.get(`${API}/leads`, { params, headers: { Authorization: `Bearer ${adminToken}` } });
      setLeads(res.data.leads || []);
      setLeadsPagination({ total: res.data.total, pages: res.data.pages });
    } catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [leadsPage, sourceFilter, adminToken]);

  useEffect(() => { fetchStats(); fetchLeads(); }, [fetchStats, fetchLeads]);

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await axios.delete(`${API}/leads/${leadId}`, { headers: getHeaders() }); toast.success("Lead deleted"); fetchLeads(); }
    catch { toast.error("Failed"); }
  };

  const handleClearCache = async () => {
    if (!window.confirm("Clear all cached reports?")) return;
    try { const res = await axios.delete(`${API}/admin/cache`, { headers: getHeaders() }); toast.success(`Cleared ${res.data.deleted} cached reports`); fetchStats(); }
    catch { toast.error("Failed to clear cache"); }
  };

  const filteredLeads = leads.filter(l => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return l.email?.toLowerCase().includes(q) || l.business_name?.toLowerCase().includes(q);
  });

  const getSourceBadge = (source) => SOURCE_BADGE[source] || "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <>
      {/* Stats Cards */}
      <section className="mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Leads", value: stats?.total_leads || 0, icon: Users, color: "text-blue-600", bg: "bg-blue-50", testid: "total-leads" },
            { label: "Today", value: stats?.leads_today || 0, icon: Calendar, color: "text-emerald-600", bg: "bg-emerald-50", testid: "leads-today" },
            { label: "This Week", value: stats?.leads_week || 0, icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50", testid: "leads-week" },
            { label: "Avg Score", value: stats?.avg_score || 0, icon: Star, color: "text-amber-600", bg: "bg-amber-50", testid: "avg-score" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <Card className="bg-white border-[#E5E7EB]">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#6B7280]">{s.label}</p>
                      <p className="text-3xl font-bold mt-1 text-[#111827]" data-testid={s.testid}>{s.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${s.bg} rounded-full flex items-center justify-center`}>
                      <s.icon className={`w-6 h-6 ${s.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-white border-[#E5E7EB]">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2 text-[#111827]"><BarChart3 className="w-5 h-5 text-blue-600" />Leads by Source</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.lead_sources && Object.keys(stats.lead_sources).length > 0 ? (
                Object.entries(stats.lead_sources).map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between">
                    <Badge className={getSourceBadge(source)}>{source || "direct"}</Badge>
                    <span className="font-medium text-[#111827]">{count}</span>
                  </div>
                ))
              ) : <p className="text-sm text-[#9CA3AF]">No source data yet</p>}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E7EB]">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2 text-[#111827]"><Database className="w-5 h-5 text-purple-600" />System Status</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between"><span className="text-[#6B7280]">Cached Audits</span><span className="font-medium text-[#111827]">{stats?.total_cached_audits || 0}</span></div>
              <div className="flex items-center justify-between"><span className="text-[#6B7280]">Stripe Mode</span><Badge className={stats?.stripe_mode === "live" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"}>{stats?.stripe_mode === "live" ? "LIVE" : "TEST"}</Badge></div>
              <Button variant="outline" size="sm" onClick={handleClearCache} className="w-full border-red-200 text-red-600 hover:bg-red-50" data-testid="clear-cache-btn">
                <Trash2 className="w-4 h-4 mr-2" />Clear All Cache
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Leads Table */}
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg text-[#111827]">Leads ({leadsPagination.total})</CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64 bg-white border-[#E5E7EB]" data-testid="search-input" />
              </div>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-36 bg-white border-[#E5E7EB]"><SelectValue placeholder="All Sources" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                  <SelectItem value="google">Google</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
                <TableHead className="text-[#6B7280]">Email</TableHead>
                <TableHead className="text-[#6B7280]">Business</TableHead>
                <TableHead className="text-center text-[#6B7280]">Score</TableHead>
                <TableHead className="text-[#6B7280]">Source</TableHead>
                <TableHead className="text-[#6B7280]">Date</TableHead>
                <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.length > 0 ? filteredLeads.map((lead, i) => (
                <TableRow key={lead.id || i} className="border-[#E5E7EB]">
                  <TableCell><span className="text-sm text-[#111827]">{lead.email}</span></TableCell>
                  <TableCell><span className="text-sm text-[#6B7280] truncate max-w-[150px] block">{lead.business_name}</span></TableCell>
                  <TableCell className="text-center">
                    {lead.overall_score ? <Badge className={lead.overall_score >= 70 ? "bg-emerald-50 text-emerald-600" : lead.overall_score >= 40 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}>{lead.overall_score}</Badge> : <span className="text-[#9CA3AF]">-</span>}
                  </TableCell>
                  <TableCell><Badge className={getSourceBadge(lead.source)}>{lead.source || "direct"}</Badge></TableCell>
                  <TableCell className="text-sm text-[#6B7280]">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    {lead.place_id && <Button variant="ghost" size="sm" onClick={() => navigate(`/report/${lead.place_id}`)} className="text-[#3B5BDB]"><ExternalLink className="w-4 h-4" /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteLead(lead.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#9CA3AF]">No leads found</TableCell></TableRow>}
            </TableBody>
          </Table>
          {leadsPagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E7EB]">
              <p className="text-sm text-[#6B7280]">Page {leadsPage} of {leadsPagination.pages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setLeadsPage(p => Math.max(1, p - 1))} disabled={leadsPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setLeadsPage(p => Math.min(leadsPagination.pages, p + 1))} disabled={leadsPage === leadsPagination.pages}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
